import { useEffect, useState } from 'react'
import { useForm, Controller, useFieldArray, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem,
  Alert, CircularProgress, IconButton, Autocomplete, Box, Typography,
} from '@mui/material'
import { Add, DeleteOutlined } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import {
  createOrder, updateOrder, getOrder, listOrderWorkPoints,
  type OrderListItem, type SaveOrderPayload, type BelowCostViolationsResponse,
} from '../../api/comenzi'
import { listClients, getClient, listSalesAgents, listClientUsualArticles } from '../../api/clienti'
import { listArticles } from '../../api/nomenclatoare'
import { listVatRates } from '../../api/taxe'
import { listWarehouses } from '../../api/depozite'
import { errorMessage } from '../../api/client'
import { useAuth } from '../../auth/AuthContext'

const lineSchema = z.object({
  articleId: z.string().min(1, 'Selectați un articol'),
  quantityThousands: z.string().min(1, 'Câmp obligatoriu'),
  pricePer1000: z.string().min(1, 'Câmp obligatoriu'),
  discountPercent: z.string().min(1, 'Câmp obligatoriu'),
  vatRateId: z.string().nullable(),
  lineDeliveryTerm: z.string().optional(),
})
const schema = z.object({
  orderDate: z.string().min(1, 'Câmp obligatoriu'),
  clientId: z.string().min(1, 'Selectați un client'),
  deliveryAddressId: z.string().min(1, 'Selectați adresa de livrare'),
  billingAddressId: z.string().min(1, 'Selectați adresa de facturare'),
  clientPoReference: z.string().optional(),
  agentUserId: z.string().min(1, 'Selectați un agent'),
  paymentTerms: z.string().optional(),
  currency: z.string().min(1, 'Câmp obligatoriu'),
  requestedDeliveryDate: z.string().min(1, 'Câmp obligatoriu'),
  notes: z.string().optional(),
  workPointId: z.string().min(1, 'Selectați punct de lucru'),
  fulfillmentWarehouseId: z.string().nullable(),
  lines: z.array(lineSchema).min(1, 'Adăugați cel puțin o linie'),
})
type FormValues = z.infer<typeof schema>

const EMPTY_LINE = { articleId: '', quantityThousands: '', pricePer1000: '', discountPercent: '0', vatRateId: null, lineDeliveryTerm: '' }
const EMPTY: FormValues = {
  orderDate: new Date().toISOString().slice(0, 10), clientId: '', deliveryAddressId: '', billingAddressId: '',
  clientPoReference: '', agentUserId: '', paymentTerms: '', currency: 'RON',
  requestedDeliveryDate: '', notes: '', workPointId: '', fulfillmentWarehouseId: null, lines: [],
}

export default function OrderFormDialog({ target, onClose }: { target: OrderListItem | null | 'new'; onClose: () => void }) {
  const open = target !== null
  const editing = target !== null && target !== 'new'
  const qc = useQueryClient()
  const { user } = useAuth()

  const { data: clients } = useQuery({ queryKey: ['comercial', 'clienti', 'toti'], queryFn: () => listClients(), enabled: open })
  const { data: agents } = useQuery({ queryKey: ['comercial', 'agenti-vanzari'], queryFn: listSalesAgents, enabled: open })
  const { data: articles } = useQuery({ queryKey: ['nomenclatoare', 'articole', 'toate'], queryFn: () => listArticles(), enabled: open })
  const { data: vatRates } = useQuery({ queryKey: ['financiar', 'taxe', 'cote-tva'], queryFn: listVatRates, enabled: open })
  const { data: warehouses } = useQuery({ queryKey: ['stocuri', 'depozite'], queryFn: listWarehouses, enabled: open })
  const { data: workPoints } = useQuery({ queryKey: ['comercial', 'comenzi', 'puncte-lucru'], queryFn: listOrderWorkPoints, enabled: open })
  const { data: detail } = useQuery({
    queryKey: ['comercial', 'comanda', editing ? (target as OrderListItem).id : null],
    queryFn: () => getOrder((target as OrderListItem).id),
    enabled: editing,
  })

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY })
  const lines = useFieldArray({ control, name: 'lines' })
  const clientId = useWatch({ control, name: 'clientId' })
  const [belowCost, setBelowCost] = useState<BelowCostViolationsResponse | null>(null)
  const [overrideReason, setOverrideReason] = useState('')

  const { data: clientDetail } = useQuery({
    queryKey: ['comercial', 'client', clientId || null], queryFn: () => getClient(clientId), enabled: !!clientId,
  })
  // FR-MDC-007 — filtrare implicită (grupare, nu excludere) la alegerea articolului.
  const { data: usualArticles } = useQuery({
    queryKey: ['comercial', 'produse-uzuale', clientId || null], queryFn: () => listClientUsualArticles(clientId), enabled: !!clientId,
  })
  const usualArticleIds = new Set(usualArticles?.map((u) => u.articleId))
  const sortedArticles = articles
    ? [...articles].sort((a, b) => Number(usualArticleIds.has(b.id)) - Number(usualArticleIds.has(a.id)))
    : []

  useEffect(() => {
    if (editing && detail) {
      reset({
        orderDate: detail.orderDate, clientId: detail.clientId, deliveryAddressId: '',
        billingAddressId: '', clientPoReference: detail.clientPoReference ?? '',
        agentUserId: detail.agentUserId, paymentTerms: detail.paymentTerms ?? '', currency: detail.currency,
        requestedDeliveryDate: detail.requestedDeliveryDate, notes: detail.notes ?? '',
        workPointId: detail.workPointId, fulfillmentWarehouseId: detail.fulfillmentWarehouseId,
        lines: detail.lines.map((l) => ({
          articleId: l.articleId, quantityThousands: String(l.quantityThousands), pricePer1000: String(l.pricePer1000),
          discountPercent: String(l.discountPercent), vatRateId: l.vatRateId, lineDeliveryTerm: l.lineDeliveryTerm ?? '',
        })),
      })
    } else if (target === 'new') {
      // FR-ADM-003a — implicit punctul de lucru activ al sesiunii curente, editabil.
      reset({ ...EMPTY, workPointId: user?.workPointId ?? '' })
    }
    setBelowCost(null)
    setOverrideReason('')
  }, [target, editing, detail, reset, user])

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload: SaveOrderPayload = {
        orderDate: values.orderDate, clientId: values.clientId, deliveryAddressId: values.deliveryAddressId,
        billingAddressId: values.billingAddressId, clientPoReference: values.clientPoReference || null,
        agentUserId: values.agentUserId, paymentTerms: values.paymentTerms || null, currency: values.currency,
        requestedDeliveryDate: values.requestedDeliveryDate, notes: values.notes || null,
        workPointId: values.workPointId, fulfillmentWarehouseId: values.fulfillmentWarehouseId || null,
        lines: values.lines.map((l) => ({
          articleId: l.articleId, quantityThousands: Number(l.quantityThousands), pricePer1000: Number(l.pricePer1000),
          discountPercent: Number(l.discountPercent), vatRateId: l.vatRateId, lineDeliveryTerm: l.lineDeliveryTerm || null,
        })),
        belowCostOverrideReason: overrideReason || null,
      }
      if (editing) return updateOrder((target as OrderListItem).id, payload)
      return createOrder(payload)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['comercial', 'comenzi'] })
      onClose()
    },
    onError: (e) => {
      if (axios.isAxiosError(e) && e.response?.status === 409 && e.response.data?.violations) {
        setBelowCost(e.response.data as BelowCostViolationsResponse)
      } else {
        setBelowCost(null)
      }
    },
  })

  const genericError = mutation.isError && !belowCost ? errorMessage(mutation.error) : null

  const deliveryAddresses = clientDetail?.addresses.filter((a) => a.type === 'Livrare') ?? []
  const billingAddresses = clientDetail?.addresses.filter((a) => a.type === 'Facturare') ?? []

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>{editing ? `Editare comandă ${(target as OrderListItem).orderNumber}` : 'Comandă nouă'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Stack direction="row" spacing={2}>
              <Controller name="orderDate" control={control} render={({ field }) => (
                <TextField {...field} type="date" label="Data comenzii" sx={{ width: 160 }} slotProps={{ inputLabel: { shrink: true } }}
                  error={!!errors.orderDate} helperText={errors.orderDate?.message} />
              )} />
              <Controller name="clientId" control={control} render={({ field }) => (
                <Autocomplete
                  sx={{ flexGrow: 1 }} options={clients?.map((c) => c.id) ?? []}
                  getOptionLabel={(id) => clients?.find((c) => c.id === id)?.name ?? id}
                  value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                  renderInput={(params) => <TextField {...params} label="Client" error={!!errors.clientId} helperText={errors.clientId?.message} />}
                />
              )} />
              <Controller name="clientPoReference" control={control} render={({ field }) => (
                <TextField {...field} label="Referință client" sx={{ width: 180 }} />
              )} />
            </Stack>
            {editing && detail && (
              <Typography variant="caption" color="text.secondary">
                Adresă salvată — livrare: {detail.deliveryAddress} · facturare: {detail.billingAddress}.
                Comanda păstrează un instantaneu text; pentru a o schimba, re-selectați mai jos.
              </Typography>
            )}
            <Stack direction="row" spacing={2}>
              <Controller name="deliveryAddressId" control={control} render={({ field }) => (
                <TextField {...field} select label="Adresă livrare" fullWidth error={!!errors.deliveryAddressId} helperText={errors.deliveryAddressId?.message}>
                  {deliveryAddresses.map((a) => <MenuItem key={a.id} value={a.id!}>{a.line}, {a.city}</MenuItem>)}
                </TextField>
              )} />
              <Controller name="billingAddressId" control={control} render={({ field }) => (
                <TextField {...field} select label="Adresă facturare" fullWidth error={!!errors.billingAddressId} helperText={errors.billingAddressId?.message}>
                  {billingAddresses.map((a) => <MenuItem key={a.id} value={a.id!}>{a.line}, {a.city}</MenuItem>)}
                </TextField>
              )} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Controller name="agentUserId" control={control} render={({ field }) => (
                <TextField {...field} select label="Agent" sx={{ width: 220 }} error={!!errors.agentUserId} helperText={errors.agentUserId?.message}>
                  {agents?.map((a) => <MenuItem key={a.id} value={a.id}>{a.fullName}</MenuItem>)}
                </TextField>
              )} />
              <Controller name="paymentTerms" control={control} render={({ field }) => (<TextField {...field} label="Termeni de plată" fullWidth />)} />
              <Controller name="currency" control={control} render={({ field }) => (
                <TextField {...field} label="Monedă" sx={{ width: 110 }} error={!!errors.currency} helperText={errors.currency?.message} />
              )} />
              <Controller name="requestedDeliveryDate" control={control} render={({ field }) => (
                <TextField {...field} type="date" label="Dată livrare solicitată" sx={{ width: 190 }} slotProps={{ inputLabel: { shrink: true } }}
                  error={!!errors.requestedDeliveryDate} helperText={errors.requestedDeliveryDate?.message} />
              )} />
              <Controller name="workPointId" control={control} render={({ field }) => (
                <TextField {...field} select label="Punct de lucru" sx={{ width: 200 }}
                  error={!!errors.workPointId} helperText={errors.workPointId?.message}>
                  {workPoints?.map((w) => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}
                </TextField>
              )} />
            </Stack>
            <Controller name="fulfillmentWarehouseId" control={control} render={({ field }) => (
              <Autocomplete
                options={warehouses?.map((w) => w.id) ?? []}
                getOptionLabel={(id) => warehouses?.find((w) => w.id === id)?.name ?? id}
                value={field.value} onChange={(_, v) => field.onChange(v)}
                renderInput={(params) => (
                  <TextField {...params} label="Depozit de onorare (opțional — activează rezervarea de stoc)" />
                )}
              />
            )} />

            {typeof errors.lines?.message === 'string' && <Alert severity="warning">{errors.lines.message}</Alert>}
            {lines.fields.map((f, i) => (
              <Stack key={f.id} direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                <Controller name={`lines.${i}.articleId`} control={control} render={({ field }) => (
                  <Autocomplete
                    sx={{ flexGrow: 1.2 }} options={sortedArticles.map((a) => a.id)}
                    groupBy={(id) => usualArticleIds.has(id) ? 'Produse uzuale' : 'Tot catalogul'}
                    getOptionLabel={(id) => { const a = articles?.find((x) => x.id === id); return a ? `${a.code} — ${a.shortDescription}` : id }}
                    value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                    renderInput={(params) => <TextField {...params} label="Articol" error={!!errors.lines?.[i]?.articleId} helperText={errors.lines?.[i]?.articleId?.message} />}
                  />
                )} />
                <Controller name={`lines.${i}.quantityThousands`} control={control} render={({ field }) => (
                  <TextField {...field} type="number" label="Cant. (mii)" sx={{ width: 110 }}
                    error={!!errors.lines?.[i]?.quantityThousands} helperText={errors.lines?.[i]?.quantityThousands?.message} />
                )} />
                <Controller name={`lines.${i}.pricePer1000`} control={control} render={({ field }) => (
                  <TextField {...field} type="number" label="Preț/1000" sx={{ width: 120 }}
                    error={!!errors.lines?.[i]?.pricePer1000} helperText={errors.lines?.[i]?.pricePer1000?.message} />
                )} />
                <Controller name={`lines.${i}.discountPercent`} control={control} render={({ field }) => (
                  <TextField {...field} type="number" label="Discount %" sx={{ width: 100 }}
                    error={!!errors.lines?.[i]?.discountPercent} helperText={errors.lines?.[i]?.discountPercent?.message} />
                )} />
                <Controller name={`lines.${i}.vatRateId`} control={control} render={({ field }) => (
                  <TextField {...field} value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value || null)} select label="TVA" sx={{ width: 110 }}>
                    <MenuItem value="">—</MenuItem>
                    {vatRates?.map((v) => <MenuItem key={v.id} value={v.id}>{v.code} ({v.ratePercent}%)</MenuItem>)}
                  </TextField>
                )} />
                <Controller name={`lines.${i}.lineDeliveryTerm`} control={control} render={({ field }) => (
                  <TextField {...field} type="date" label="Termen livrare" sx={{ width: 160 }} slotProps={{ inputLabel: { shrink: true } }} />
                )} />
                <IconButton onClick={() => lines.remove(i)} sx={{ mt: 0.5 }}><DeleteOutlined fontSize="small" /></IconButton>
              </Stack>
            ))}
            <Box>
              <Button size="small" startIcon={<Add />} onClick={() => lines.append(EMPTY_LINE)}>Adaugă linie</Button>
            </Box>

            <Controller name="notes" control={control} render={({ field }) => (<TextField {...field} label="Observații" fullWidth multiline minRows={2} />)} />

            {belowCost && (
              <>
                <Alert severity="warning">
                  {belowCost.violations.map((v) => (
                    <div key={v.articleCode}>{v.articleCode}: preț {v.pricePer1000.toFixed(4).replace('.', ',')} sub costul standard {v.standardCost.toFixed(4).replace('.', ',')}</div>
                  ))}
                </Alert>
                <TextField label="Motiv suprascriere preț sub cost" fullWidth multiline minRows={2}
                  value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} />
              </>
            )}
            {Object.keys(errors).length > 0 && (
              <Alert severity="warning">Există câmpuri obligatorii necompletate — verificați câmpurile marcate cu roșu mai sus.</Alert>
            )}
            {genericError && <Alert severity="error">{genericError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending || (!!belowCost && !overrideReason.trim())}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            {belowCost ? 'Salvează cu suprascriere' : 'Salvează'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
